import { createAsyncThunk } from "@reduxjs/toolkit";
import api, { TOKEN } from "../../api/axiosInstance";
import {
  chatChunkAppended,
  chatCompleted,
  chatStreamErrored,
  chatStreamStarted,
} from "./slice";

export const askMyJournal = createAsyncThunk(
  "ai/askMyJournal",
  async (formInfo, { rejectWithValue }) => {
    try {
      const { question, doc_types } = formInfo;
      const res = await api.post(
        "ai/journal/ask",
        { question, doc_types },
        { headers: { "Content-Type": "application/json" } }
      );
      return res.data;
    } catch (error) {
      console.error(error);
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

/**
 * Streaming variant of askMyJournal.
 *
 * Hits POST /ai/journal/ask/stream and parses Server-Sent Events
 * incrementally with fetch + ReadableStream (axios doesn't expose
 * the underlying reader, and EventSource doesn't support POST or
 * custom Authorization headers — both leave fetch as the only
 * option). Dispatches lifecycle actions directly:
 *
 *   - chatStreamStarted on submit (clears prior answer, sets loading)
 *   - chatChunkAppended per `chunk` event (token delta)
 *   - chatCompleted on `complete` event (full structured payload —
 *     overrides any in-progress streaming text with the humanized
 *     answer)
 *   - chatStreamErrored on `error` / `quota_exceeded` events or
 *     network failure
 *
 * Plain function instead of createAsyncThunk: the streaming flow has
 * many intermediate "success" events, which doesn't fit the
 * pending/fulfilled/rejected lifecycle.
 */
export const askMyJournalStream = (formInfo) => async (dispatch) => {
  dispatch(chatStreamStarted());

  const { question, doc_types } = formInfo;
  const token = window.localStorage.getItem(TOKEN);
  const baseUrl = process.env.REACT_APP_API_URL || "";
  const url = `${baseUrl}ai/journal/ask/stream`;

  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify({ question, doc_types }),
    });
  } catch (e) {
    dispatch(chatStreamErrored({ answer: e.message || "Network error" }));
    return;
  }

  if (!response.ok || !response.body) {
    let errorPayload;
    try {
      errorPayload = await response.json();
    } catch (_e) {
      errorPayload = { answer: `Request failed (${response.status})` };
    }
    dispatch(chatStreamErrored(errorPayload));
    return;
  }

  // Parse SSE incrementally. Events are separated by blank lines
  // (`\n\n`); each event has `event:` and `data:` header lines.
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const handleEvent = (rawEvent) => {
    let eventName = "";
    let dataLine = "";
    for (const line of rawEvent.split("\n")) {
      if (line.startsWith("event: ")) {
        eventName = line.slice(7).trim();
      } else if (line.startsWith("data: ")) {
        // Append; SSE allows multi-line `data:` continuation.
        dataLine += line.slice(6);
      }
    }
    if (!eventName || !dataLine) return;

    let parsed;
    try {
      parsed = JSON.parse(dataLine);
    } catch (_e) {
      return;
    }

    if (eventName === "chunk") {
      dispatch(chatChunkAppended({ delta: parsed.delta || "" }));
    } else if (eventName === "complete") {
      dispatch(chatCompleted(parsed));
    } else if (eventName === "error" || eventName === "quota_exceeded") {
      const errored = { ...parsed };
      if (eventName === "quota_exceeded") errored.quota_exceeded = true;
      dispatch(chatStreamErrored(errored));
    }
  };

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let split;
      while ((split = buffer.indexOf("\n\n")) !== -1) {
        const rawEvent = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);
        handleEvent(rawEvent);
      }
    }
    // Flush any remainder (shouldn't normally happen — backend always
    // ends with a `complete` or `error` event followed by `\n\n`).
    if (buffer.trim()) handleEvent(buffer);
  } catch (e) {
    dispatch(chatStreamErrored({ answer: e.message || "Stream interrupted" }));
  }
};

export const fixCsvImport = createAsyncThunk(
  "ai/fixCsvImport",
  async (formInfo, { rejectWithValue }) => {
    try {
      const { csvFile } = formInfo;
      const formData = new FormData();
      formData.append("csv_file", csvFile);
      const res = await api.post("ai/csv/fix", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    } catch (error) {
      console.error(error);
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const indexUserData = createAsyncThunk(
  "ai/indexUserData",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.post("ai/index", {});
      return res.data;
    } catch (error) {
      console.error(error);
      return rejectWithValue(error);
    }
  }
);
